import { CredentialInformation, WebAuthnKeyAuthenticate, WebAuthnKeyRegister, AclChange, ActivityLogInfo, ActivityLogMetadata, UserProfile } from "@preveil-api";
import {
  AccountUserKey, AppConfiguration, AuthenticationTypes, GlobalConstants, NodePermissionLabels, SettingsErrorMessages, WebAuthnErrorMessages, Helpers, ACLRoles, ActivityLog,
  BrowserType
} from "src/common";
import * as cbor from "cbor";

export const ExportOption = {
  email: "Email",
  drive: "Drive",
  logs: "Logs",
  acl_report: "ACL Report",
};

// ----------------------------------------------------------------------------
// SETTINGS HELPERS
// ----------------------------------------------------------------------------

// Description: Authenticates an existing WebAuthn Key with the browser
// https://webauthn.guide/#registration
export async function authenticateWebAuthnKey(cred_info: CredentialInformation, use_new_challenge?: boolean): Promise<WebAuthnKeyAuthenticate | undefined> {
  const config = new AppConfiguration();
  const decoded_context = JSON.parse(atob(cred_info.context));
  const allow_credentials: PublicKeyCredentialDescriptor[] = [
    {
      id: Uint8Array.from(Helpers.b64Decode(decoded_context.id)), // This should be the stored credential ID from your backend
      type: "public-key",
      transports: decoded_context.type === AuthenticationTypes.SECURITY_KEY ? ["usb", "nfc", "ble"] :
        decoded_context.type === AuthenticationTypes.PASSKEY ? config.browser === BrowserType.firefox ? [] :
          ["hybrid" as AuthenticatorTransport] : [] // hybrid does not exist on firefox.
    }
  ];
  const challenge = use_new_challenge ? cred_info.newchallenge : cred_info.challenge;
  const cred_request_options = {
    challenge: Helpers.b64Decode(challenge).buffer as ArrayBuffer, // Ensure it's an ArrayBuffer
    timeout: GlobalConstants.WEB_AUTH_TIMEOUT, // Default: 2 minutes
    allowCredentials: allow_credentials,
    authenticatorSelection: {
      authenticatorAttachment: "cross-platform" as AuthenticatorAttachment, // "platform" (device) or "cross-platform" (external keys)
      residentKey: "preferred" as ResidentKeyRequirement,
      requireResidentKey: false,
      userVerification: "preferred" as UserVerificationRequirement, // "required", "preferred", or "discouraged"
    }
  };
  const credential = await navigator.credentials.get({ publicKey: cred_request_options });
  if (!!credential) {
    const public_credential = credential as PublicKeyCredential;
    const info = public_credential.response as AuthenticatorAssertionResponse;
    const params: WebAuthnKeyAuthenticate = {
      client_data: Helpers.b64Encode(new Uint8Array(info.clientDataJSON)),
      auth_data: Helpers.b64Encode(new Uint8Array(info.authenticatorData)),
      signature: Helpers.b64Encode(new Uint8Array(info.signature)),
    };
    return params;
  }
}

// Description: Registers a new Web Authn Key with the browser
// https://webauthn.guide/#registration
export async function registerWebAuthnKey(user_id: string, display_name: string): Promise<WebAuthnKeyRegister | undefined> {
  const user_id_byes = Helpers.b64Decode(btoa(user_id));
  const _id = new Uint8Array(64);
  _id.set(user_id_byes.slice(0, 32), 0);
  _id.set(await Helpers.sha256Checksum(user_id_byes), 32);

  const cred_create_options = {
    challenge: Uint8Array.from(window.crypto.getRandomValues(new Uint8Array(32)), c => c).buffer,
    // Relying Party (RP) information (your application)
    rp: {
      name: "PreVeil", // Human-readable name of your app
      id: window.location.hostname,
    },
    // User information (the person registering the passkey)
    user: {
      id: _id, // A unique ID for the user
      name: user_id, // User's email or username
      displayName: display_name, // Friendly display name
    },
    // Supported public key algorithms (most common: ES256 and RS256)
    pubKeyCredParams: [
      { type: "public-key", alg: -7 },   // ES256 (Elliptic Curve P-256)
      { type: "public-key", alg: -257 } // RS256 (RSA 2048)
    ] as PublicKeyCredentialParameters[],
    timeout: GlobalConstants.WEB_AUTH_TIMEOUT, // Default: 2 minutes
    authenticatorSelection: {
      authenticatorAttachment: "cross-platform" as AuthenticatorAttachment, // "platform" (device) or "cross-platform" (external keys)
      residentKey: "preferred" as ResidentKeyRequirement,
      requireResidentKey: false,
      userVerification: "preferred" as UserVerificationRequirement, // "required", "preferred", or "discouraged"
    },
    mediation: "required" as AuthenticatorSelectionCriteria,
    attestation: "direct" as AttestationConveyancePreference,
  };
  const credential = await navigator.credentials.create({ publicKey: cred_create_options });
  if (!!credential) {
    const public_credential = credential as PublicKeyCredential;
    const _response = (credential as PublicKeyCredential).response as any; // as AuthenticatorAttestationResponse;
    const public_key = _response.getPublicKey();
    const public_key_algorithm = _response.getPublicKeyAlgorithm();
    let key_type: string = AuthenticationTypes.WEBAUTHN_CREDENTIAL;
    if (_response.getTransports().includes("internal") || _response.getTransports().includes("hybrid")) {
      key_type = AuthenticationTypes.PASSKEY;
    } else if (_response.getTransports().includes("usb") || _response.getTransports().includes("nfc") || _response.getTransports().includes("ble")) {
      key_type = AuthenticationTypes.SECURITY_KEY;
    }
    const context = {
      id: Helpers.b64Encode(new Uint8Array(public_credential.rawId)),
      type: key_type
    };
    const attestation_object = new Uint8Array((public_credential.response as AuthenticatorAttestationResponse).attestationObject);
    const decoded_attestation = !!attestation_object && attestation_object.length > 0 ? cbor.decode(attestation_object) : undefined;
    const auth_data = !!decoded_attestation ? new Uint8Array(decoded_attestation.authData) : undefined;
    const sign_count = !!auth_data ? new DataView(auth_data.buffer, 33, 4).getUint32(0, false) : 0;
    if (!!public_key && !!public_key_algorithm) {
      const payload = {
        public_key: Helpers.b64Encode(new Uint8Array(public_key)),
        public_key_algorithm,
        context: btoa(JSON.stringify(context)),
        sign_count,
        check_sign_count: sign_count > 0
      };
      return payload;
    }
  }
}

// Description: Get the error message that corresponds to the web auth API error.
export function getWebAuthErrorMessage(error: Error): string {
  switch (error.name) {
    case "AbortError":
      return WebAuthnErrorMessages.abort_error;
    case "SecurityError":
      return WebAuthnErrorMessages.security_error;
    case "InvalidStateError":
      return WebAuthnErrorMessages.invalid_state_error;
    case "ConstraintError":
    case "QuotaExceededError":
      return WebAuthnErrorMessages.quota_exceeded;
    case "NetworkError":
      return WebAuthnErrorMessages.network_error;
    case "NotAllowedError":
    case "NotSupportedError":
      return WebAuthnErrorMessages.not_allowed_error;
    default:
      return SettingsErrorMessages.default;
  }
}

export async function encryptPhoneNumber(phone_number: string, user_key: AccountUserKey): Promise<string | undefined> {
  try {
    const encoded = Helpers.utf8Encode(phone_number);
    return Helpers.b64Encode(await user_key.encryption_key.seal(encoded));
  } catch (error) {
    console.error("Failed to encrypt phone number:", error);
    return undefined;
  }
}

// ----------------------------------------------------------------------------
// ACTIVITY LOG HELPERS
// ----------------------------------------------------------------------------

// Description: Map a granted roles array to a human-readable permission label.
// Handles both full grants and promotion scenarios (partial grants).
// Mirrors Go's createPermissionFromACLChange function.
export function createPermissionFromACLChange(roles: string[]): string | null {
  const role_set = new Set(roles.map((r) => r.toLowerCase()));
  const has_writer = role_set.has("writer");
  const has_reader = role_set.has("reader");
  const has_acl_reader = role_set.has("acl_reader");
  const has_acl_writer = role_set.has("acl_writer");
  const has_viewer = role_set.has("viewer");

  // Edit & Share granted (writer, reader, acl_reader, acl_writer)
  if (has_writer && has_reader && has_acl_reader && has_acl_writer) return NodePermissionLabels.edit_and_share;
  // Edit granted (writer, reader, acl_reader)
  if (has_writer && has_reader && has_acl_reader && !has_acl_writer) return NodePermissionLabels.edit;
  // Read Only granted (reader only)
  if (has_reader && !has_writer && !has_acl_reader && !has_acl_writer && !has_viewer) return NodePermissionLabels.read_only;
  // View Only granted (viewer only)
  if (has_viewer && !has_reader && !has_writer && !has_acl_reader && !has_acl_writer) return NodePermissionLabels.view_only;
  // Promotion: Edit -> Edit & Share (only acl_writer granted)
  if (has_acl_writer && !has_writer && !has_reader && !has_acl_reader && !has_viewer) return NodePermissionLabels.edit_and_share;
  // Promotion: Read Only -> Edit & Share (writer, acl_reader, acl_writer granted)
  if (has_writer && has_acl_reader && has_acl_writer && !has_reader && !has_viewer) return NodePermissionLabels.edit_and_share;
  // Promotion: Read Only -> Edit (writer, acl_reader granted)
  if (has_writer && has_acl_reader && !has_reader && !has_acl_writer && !has_viewer) return NodePermissionLabels.edit;

  return null;
}

// Description: Return the permission category (change type and permission level) based on role changes.
// granted, view_only, edit_only, read_only, removed.
export function getUserPermissionType(
  changes: AclChange[],
  user_id: string
): string | null {
  const viewer_role = "viewer";
  const all_acl_roles_count = Object.keys(ACLRoles).length;

  const user_changes = changes.filter(
    (c: AclChange) => c.user_id.toLowerCase() === user_id.toLowerCase()
  );

  if (user_changes.length === 0) return null;

  const granted_change = user_changes.find((c) => c.change === "granted");
  if (granted_change) {
    const is_viewer_only =
      granted_change.roles.length === 1 && granted_change.roles.includes(viewer_role);
    return is_viewer_only ? "view_only" : "granted";
  }

  const removed_change = user_changes.find((c) => c.change === "removed");
  if (!removed_change) return null;

  const { roles } = removed_change;

  // Viewer-only removal is always "removed"
  if (roles.length === 1 && roles[0] === viewer_role) {
    return "removed";
  }

  const was_granted_viewer = user_changes.some(
    (c) => c.change === "granted" && c.roles.includes(viewer_role)
  );

  // Full removal: all roles or reader removed
  if (roles.length === all_acl_roles_count || roles.includes(ACLRoles.reader)) {
    return was_granted_viewer ? "view_only" : "removed";
  }

  // Permission downgrade - check remaining roles
  const active_roles = new Set(
    Object.keys(ACLRoles).filter((role: string) => !roles.includes(role))
  );

  if (active_roles.has(ACLRoles.reader)) {
    const has_writer = active_roles.has(ACLRoles.writer);
    const has_acl_reader = active_roles.has(ACLRoles.acl_reader);

    if (has_writer && has_acl_reader && !active_roles.has(ACLRoles.acl_writer)) {
      return "edit_only";
    }
    if (!has_writer && !has_acl_reader) {
      return "read_only";
    }
  }

  // Default: if user was granted viewer, return view_only; otherwise removed
  return was_granted_viewer ? "view_only" : "removed";
}

// Description: Group ACL changes by user, excluding the actor (the user who performed the action)
// Returns a Map where keys are lowercase user_ids and values are arrays of their changes
export function groupAclChangesByUser(
  changes: AclChange[],
  actor_id: string
): Map<string, AclChange[]> {
  const user_changes_map = new Map<string, AclChange[]>();

  changes.forEach((c: AclChange) => {
    const user_id = c.user_id.toLowerCase();
    if (user_id === actor_id) return;

    if (!user_changes_map.has(user_id)) {
      user_changes_map.set(user_id, []);
    }
    const user_changes = user_changes_map.get(user_id);
    if (user_changes) {
      user_changes.push(c);
    }
  });

  return user_changes_map;
}

// Description: Check if an acl_change should be split into separate log entries
// If there are multiple users with different permission changes in the same event,
// we should split the event into separate log entries for better readability.
// example: 2 users with view_only and 1 user with read_only
export function shouldSplitAclChange(
  action: string,
  metadata: ActivityLogMetadata,
  user: UserProfile
): boolean {
  if (action !== "acl_change") return false;

  const changes = metadata.acl_changes;
  if (!changes || changes.length <= 1) return false;

  // Actor is the user who performed the action (granted/removed permissions)
  // This is different from the user_id which is the user viewing the logs
  const actor_id = user.address.toLowerCase();
  const unique_users = new Set(
    changes
      .map((c: AclChange) => c.user_id.toLowerCase())
      .filter((id) => id !== actor_id)
  );

  if (unique_users.size <= 1) return false;

  // Check if all users have the same permission category
  const categories = new Set(
    [...unique_users]
      .map((user_id) => getUserPermissionType(changes, user_id))
      .filter(Boolean)
  );

  if (categories.size > 1) return true;

  // If all users are in the "granted" category, also split by permission label
  // so users granted different permissions get separate log entries
  if (categories.size === 1 && categories.has("granted")) {
    const user_changes_map = groupAclChangesByUser(changes, actor_id);
    const granted_labels = new Set(
      [...user_changes_map.entries()]
        .map(([, user_acl_changes]) => {
          const granted_change = user_acl_changes.find((c: AclChange) => c.change === "granted");
          return granted_change ? createPermissionFromACLChange(granted_change.roles) : null;
        })
        .filter(Boolean)
    );
    return granted_labels.size > 1;
  }

  return false;
}

// Description: Create separate log entries grouped by permission category
// Users with the same permission change are grouped together in one event
export function createSplitAclChangeLogs(
  activity_log: ActivityLog
): ActivityLogInfo[] {
  if (!shouldSplitAclChange(activity_log.action, activity_log.metadata, activity_log.user)) {
    return [activity_log.getActivityLogInfo()];
  }

  const changes = activity_log.metadata.acl_changes;
  // Actor is the user who performed the action (granted/removed permissions)
  // This is different from activity_log.user_id which is the user viewing the logs
  const actor_id = activity_log.user.address.toLowerCase();

  // Group changes by user, excluding actor
  const user_changes_map = groupAclChangesByUser(changes, actor_id);

  // Group users by permission category.
  // For "granted" users, further sub-group by permission label so users
  // granted different permissions produce separate log entries.
  const category_changes_map = new Map<string, AclChange[]>();
  user_changes_map.forEach((user_changes, user_key) => {
    const category = getUserPermissionType(changes, user_key);
    if (!category) return;

    let map_key = category;
    if (category === "granted") {
      const granted_change = user_changes.find((c: AclChange) => c.change === "granted");
      if (granted_change) {
        const permission_label = createPermissionFromACLChange(granted_change.roles);
        !!permission_label && (map_key = `granted_${permission_label}`);
      }
    }

    if (!category_changes_map.has(map_key)) {
      category_changes_map.set(map_key, []);
    }
    const category_changes = category_changes_map.get(map_key);
    if (category_changes) {
      category_changes.push(...user_changes);
    }
  });

  // Create a log entry for each category
  const split_logs: ActivityLogInfo[] = [];
  category_changes_map.forEach((combined_changes) => {
    const split_log = new ActivityLog(
      activity_log.uid,
      activity_log.timestamp,
      activity_log.collection_id,
      activity_log.topic,
      activity_log.action,
      activity_log.device,
      activity_log.user,
      { ...activity_log.metadata, acl_changes: combined_changes },
      activity_log.global_idx,
      activity_log.user_id,
      activity_log.is_admin,
      activity_log.do_not_show
    );
    split_logs.push(split_log.getActivityLogInfo());
  });

  return split_logs;
}

// Parse the Approval Group Request payload data that comes from Filesync log events to get additional data
// about the event (ex: users emails)
export function parseAgRequestPayload(payload_data: string, ag_type: string): string {
  const payload = JSON.parse(payload_data);
  const log_type: string[] = [];
  let str = "";

  switch (ag_type) {
    case "export":
      if (payload.data.include_emails) {
        log_type.push(`<span class="fw-600">${ExportOption.email}</span>`);
      }
      if (payload.data.include_files) {
        log_type.push(`<span class="fw-600">${ExportOption.drive}</span>`);
      }
      if (payload.data.include_logs) {
        log_type.push(`<span class="fw-600">${ExportOption.logs}</span>`);
      }
      if (payload.data.include_org_acl_report) {
        log_type.push(`<span class="fw-600">${ExportOption.acl_report}</span>`);
      }
      str += " for exporting ";
      str = str.concat(log_type.join(" & "));
      str += " for ";

      const users: Array<{ user_id: string; key_version: number }> = payload.data.users;
      users.forEach((user: { user_id: string; key_version: number }, index: number) => {
        str = str.concat(`<span class="fw-600">${user.user_id}</span>`);
        if (index < users.length - 1) {
          str = str.concat(", ");
        }
      });
      break;
    case "member_rekey_and_set_approval_group":
      const ag_users = payload.data.events.map((user_event: any) => `<span class="fw-600">${user_event.user_id}</span>`);
      const is_multiple_users = ag_users.length > 1;
      const template = is_multiple_users ? 'users' : 'user';
      str = `to change the recovery group for the following ${template}: ${ag_users.join(', ')}`;
      break;
    default:
      break;
  }

  return str;
}